import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

type NavbarProps = {
  hideAuthButtons?: boolean;
  staticHeader?: boolean;
};

const NAV_LINKS = [
  { label: "Write Your Book", to: "/write-your-book" },
  { label: "Manuscript Review", to: "/first-draft" },
  { label: "Extend", to: "/extend" },
  { label: "Pricing", to: "/pricing" },
  { label: "Publishers", to: "/publishers" },
  { label: "Events", to: "/events" },
  { label: "Author Promotion", to: "/author-promotion" },
  { label: "FAQs", to: "/#faq" },
];

const Navbar = ({ hideAuthButtons = false }: NavbarProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => {
    if (path.startsWith("/#")) {
      return location.pathname === "/" && location.hash === path.substring(1);
    }
    return location.pathname === path;
  };

  const logout = async () => {
    await signOut();
    navigate("/");
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.hash]);

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b",
        scrolled
          ? "bg-background/92 backdrop-blur-xl border-border"
          : "bg-background/70 backdrop-blur-md border-transparent"
      )}
    >
      <div className="container mx-auto px-4 flex h-16 sm:h-[72px] items-center justify-between gap-6">
        <Link to="/" className="flex items-center shrink-0" aria-label="AuthorPro home">
          <img
            src="/lovable-uploads/5e857869-ad31-4667-aad3-34f78fe224ad.png"
            alt="AuthorPro"
            className="h-8 sm:h-9 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Main">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={cn(
                "relative px-3 py-2 text-[13px] font-medium tracking-wide transition-colors",
                isActive(link.to)
                  ? "text-gold"
                  : "text-foreground/70 hover:text-foreground"
              )}
            >
              {link.label}
              {isActive(link.to) && (
                <span className="absolute inset-x-3 -bottom-0.5 h-px bg-gold" aria-hidden />
              )}
            </Link>
          ))}
        </nav>

        {!hideAuthButtons && (
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                <Button asChild variant="ghost" className="text-sm text-foreground/80">
                  <Link to="/">Home</Link>
                </Button>
                <Button onClick={logout} variant="outline" className="text-sm">
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button
                  asChild
                  className="text-sm bg-gold text-gold-foreground hover:bg-gold-light font-semibold"
                >
                  <a
                    href="https://meetings.series-a.co/meetings/series-a/authorpro"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book a Call
                  </a>
                </Button>
                <Button asChild variant="outline" className="text-sm">
                  <Link to="/sign-in">Sign In</Link>
                </Button>
              </>
            )}
          </div>
        )}

        <button
          type="button"
          className="lg:hidden p-2 text-foreground"
          onClick={() => setMobileMenuOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-border bg-background/98 backdrop-blur-xl">
          <div className="container mx-auto px-4 py-4 flex flex-col">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  "py-3 text-[15px] border-b border-border/60",
                  isActive(link.to) ? "text-gold" : "text-foreground/80"
                )}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 flex flex-col gap-3">
              <Button asChild className="w-full bg-gold text-gold-foreground hover:bg-gold-light font-semibold">
                <a
                  href="https://meetings.series-a.co/meetings/series-a/authorpro"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Book a Call
                </a>
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link to="/sign-in">Sign In</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
